// Permission definitions - Fine-grained permission points
export enum Permission {
  // === Booking Management Permissions ===
  VIEW_BOOKINGS = 'view_bookings',           // View booking list
  CREATE_BOOKING = 'create_booking',         // Create walk-in booking
  EDIT_BOOKING = 'edit_booking',             // Edit booking info
  CANCEL_BOOKING = 'cancel_booking',         // Cancel booking
  CONFIRM_BOOKING = 'confirm_booking',       // Confirm booking
  MANAGE_CALENDAR = 'manage_calendar',       // Drag and drop booking adjustment
  MANAGE_PROGRAMMES = 'manage_programmes',   // Manage programme scheduling
  MANAGE_PRIVATE_LESSONS = 'manage_private_lessons', // Manage private lesson scheduling
  USE_AI_ASSISTANT = 'use_ai_assistant',     // Legacy AI access control
  READ_AI_ASSISTANT = 'read_ai_assistant',   // Read Nexcave AI data
  WRITE_AI_ASSISTANT = 'write_ai_assistant', // Write Nexcave AI data
  EXPORT_AI_DATA = 'export_ai_data',         // Export Nexcave AI data

  // === Financial Permissions ===
  VIEW_REVENUE = 'view_revenue',             // View revenue data 🚨
  HANDLE_PAYMENTS = 'handle_payments',       // Handle payments
  PROCESS_REFUNDS = 'process_refunds',       // Process refunds
  VIEW_FINANCIAL_REPORTS = 'view_financial_reports', // Financial reports 🚨

  // === Customer Management Permissions ===
  VIEW_CUSTOMERS = 'view_customers',         // View customer info
  EDIT_CUSTOMERS = 'edit_customers',         // Edit customer info
  VIEW_CUSTOMER_HISTORY = 'view_customer_history', // Customer history

  // === Court Management Permissions ===
  MANAGE_COURTS = 'manage_courts',           // Manage court settings
  MANAGE_PRICING = 'manage_pricing',         // Pricing management 🚨

  // === Staff Management Permissions ===
  VIEW_STAFF = 'view_staff',                 // View staff list
  MANAGE_STAFF = 'manage_staff',             // Manage staff accounts 🚨
  ASSIGN_PERMISSIONS = 'assign_permissions', // Assign permissions 🚨

  // === Content Management Permissions ===
  MANAGE_CONTENT = 'manage_content',         // Manage website content 🚨

  // === System Management Permissions ===
  SYSTEM_SETTINGS = 'system_settings',       // System settings 🚨
  VIEW_ANALYTICS = 'view_analytics',         // Data analytics
  EXPORT_DATA = 'export_data',               // Data export 🚨
}

// Role definitions
export enum Role {
  ADMIN = 'admin',           // Super Administrator
  MANAGER = 'manager',       // Venue Manager  
  SUPERVISOR = 'supervisor', // Supervisor
  STAFF = 'staff',           // Regular Staff
  TRAINEE = 'trainee'        // Trainee
}

// Permission group definitions - Remove hardcoded names, use translation keys in UI
export const PERMISSION_GROUPS: Record<string, { icon: string; name: string; permissions: Permission[] }> = {
  booking: {
    icon: 'calendar',
    name: '预订管理',
    permissions: [
      Permission.VIEW_BOOKINGS,
      Permission.CREATE_BOOKING,
      Permission.EDIT_BOOKING,
      Permission.CANCEL_BOOKING,
      Permission.CONFIRM_BOOKING,
      Permission.MANAGE_CALENDAR,
    ]
  },
  scheduling: {
    icon: 'timeline',
    name: '排课管理',
    permissions: [
      Permission.MANAGE_PROGRAMMES,
      Permission.MANAGE_PRIVATE_LESSONS,
    ]
  },
  financial: {
    icon: 'dollar-sign',
    name: '财务权限',
    permissions: [
      Permission.VIEW_REVENUE,
      Permission.HANDLE_PAYMENTS,
      Permission.PROCESS_REFUNDS,
      Permission.VIEW_FINANCIAL_REPORTS,
    ]
  },
  customer: {
    icon: 'users',
    name: '客户管理',
    permissions: [
      Permission.VIEW_CUSTOMERS,
      Permission.EDIT_CUSTOMERS,
      Permission.VIEW_CUSTOMER_HISTORY,
    ]
  },
  court: {
    icon: 'map-pin',
    name: '场地管理',
    permissions: [
      Permission.MANAGE_COURTS,
      Permission.MANAGE_PRICING,
    ]
  },
  staff: {
    icon: 'shield',
    name: '员工管理',
    permissions: [
      Permission.VIEW_STAFF,
      Permission.MANAGE_STAFF,
      Permission.ASSIGN_PERMISSIONS,
    ]
  },
  content: {
    icon: 'file-text',
    name: '内容管理',
    permissions: [
      Permission.MANAGE_CONTENT,
    ]
  },
  ai: {
    icon: 'sparkles',
    name: 'Nexcave AI',
    permissions: [
      Permission.READ_AI_ASSISTANT,
      Permission.WRITE_AI_ASSISTANT,
      Permission.EXPORT_AI_DATA,
      Permission.USE_AI_ASSISTANT,
    ],
  },
  system: {
    icon: 'settings',
    name: '系统设置',
    permissions: [
      Permission.SYSTEM_SETTINGS,
      Permission.VIEW_ANALYTICS,
      Permission.EXPORT_DATA,
    ]
  }
};

// Role permission mapping
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  [Role.ADMIN]: Object.values(Permission) as Permission[],
  
  [Role.MANAGER]: [
    // Business management permissions (no super admin privileges)
    Permission.VIEW_BOOKINGS,
    Permission.CREATE_BOOKING,
    Permission.EDIT_BOOKING,
    Permission.CANCEL_BOOKING,
    Permission.CONFIRM_BOOKING,
    Permission.MANAGE_CALENDAR,
    Permission.MANAGE_PROGRAMMES,
    Permission.MANAGE_PRIVATE_LESSONS,
    Permission.VIEW_REVENUE,
    Permission.HANDLE_PAYMENTS,
    Permission.PROCESS_REFUNDS,
    Permission.VIEW_FINANCIAL_REPORTS,
    Permission.VIEW_CUSTOMERS,
    Permission.EDIT_CUSTOMERS,
    Permission.VIEW_CUSTOMER_HISTORY,
    Permission.MANAGE_COURTS,
    Permission.MANAGE_PRICING,
    Permission.VIEW_STAFF,
    Permission.MANAGE_CONTENT,
    Permission.READ_AI_ASSISTANT,
    Permission.WRITE_AI_ASSISTANT,
    Permission.EXPORT_AI_DATA,
    Permission.USE_AI_ASSISTANT,
    Permission.VIEW_ANALYTICS,
    Permission.EXPORT_DATA,
  ],
  
  [Role.SUPERVISOR]: [
    // Operations management permissions (no financial sensitive permissions)
    Permission.VIEW_BOOKINGS,
    Permission.CREATE_BOOKING,
    Permission.EDIT_BOOKING,
    Permission.CANCEL_BOOKING,
    Permission.CONFIRM_BOOKING,
    Permission.MANAGE_CALENDAR,
    Permission.MANAGE_PROGRAMMES,
    Permission.MANAGE_PRIVATE_LESSONS,
    Permission.HANDLE_PAYMENTS,
    Permission.VIEW_CUSTOMERS,
    Permission.EDIT_CUSTOMERS,
    Permission.VIEW_CUSTOMER_HISTORY,
    Permission.VIEW_ANALYTICS,
    Permission.READ_AI_ASSISTANT,
    Permission.USE_AI_ASSISTANT,
  ],
  
  [Role.STAFF]: [
    // Basic operation permissions
    Permission.VIEW_BOOKINGS,
    Permission.CREATE_BOOKING,
    Permission.CONFIRM_BOOKING,
    Permission.MANAGE_PRIVATE_LESSONS,
    Permission.HANDLE_PAYMENTS,
    Permission.VIEW_CUSTOMERS,
    Permission.VIEW_CUSTOMER_HISTORY,
    Permission.READ_AI_ASSISTANT,
    Permission.USE_AI_ASSISTANT,
  ],
  
  [Role.TRAINEE]: [
    // Read-only permissions
    Permission.VIEW_BOOKINGS,
    Permission.VIEW_CUSTOMERS,
  ]
};

// Permission description mapping
export const PERMISSION_DESCRIPTIONS: Record<Permission, string> = {
  [Permission.VIEW_BOOKINGS]: 'View booking list and details',
  [Permission.CREATE_BOOKING]: 'Create walk-in booking',
  [Permission.EDIT_BOOKING]: 'Edit booking information',
  [Permission.CANCEL_BOOKING]: 'Cancel booking',
  [Permission.CONFIRM_BOOKING]: 'Confirm booking',
  [Permission.MANAGE_CALENDAR]: 'Drag and drop booking time adjustment',
  [Permission.MANAGE_PROGRAMMES]: 'Manage programme schedules, enrollment and class rules',
  [Permission.MANAGE_PRIVATE_LESSONS]: 'Manage private coaching schedules and participants',
  [Permission.READ_AI_ASSISTANT]: 'Read Nexcave AI data',
  [Permission.WRITE_AI_ASSISTANT]: 'Write Nexcave AI data',
  [Permission.EXPORT_AI_DATA]: 'Export Nexcave AI data',
  [Permission.USE_AI_ASSISTANT]: 'Use AI assistant for operational commands',
  [Permission.VIEW_REVENUE]: 'View revenue data',
  [Permission.HANDLE_PAYMENTS]: 'Handle payments',
  [Permission.PROCESS_REFUNDS]: 'Process refunds',
  [Permission.VIEW_FINANCIAL_REPORTS]: 'View financial reports',
  [Permission.VIEW_CUSTOMERS]: 'View customer information',
  [Permission.EDIT_CUSTOMERS]: 'Edit customer information',
  [Permission.VIEW_CUSTOMER_HISTORY]: 'View customer history',
  [Permission.MANAGE_COURTS]: 'Manage court settings',
  [Permission.MANAGE_PRICING]: 'Pricing management',
  [Permission.VIEW_STAFF]: 'View staff list',
  [Permission.MANAGE_STAFF]: 'Manage staff accounts',
  [Permission.ASSIGN_PERMISSIONS]: 'Assign permissions',
  [Permission.MANAGE_CONTENT]: 'Manage website content',
  [Permission.SYSTEM_SETTINGS]: 'System Settings',
  [Permission.VIEW_ANALYTICS]: 'Data analytics',
  [Permission.EXPORT_DATA]: 'Data export',
};

// Utility function: Get role default permissions
export const getRolePermissions = (role: Role): Permission[] => {
  return ROLE_PERMISSIONS[role] || [];
};

// Utility function: Check if permission is sensitive
export const isSensitivePermission = (permission: Permission): boolean => {
  const sensitivePermissions = [
    Permission.VIEW_REVENUE,
    Permission.VIEW_FINANCIAL_REPORTS,
    Permission.MANAGE_PRICING,
    Permission.MANAGE_STAFF,
    Permission.ASSIGN_PERMISSIONS,
    Permission.MANAGE_CONTENT,
    Permission.WRITE_AI_ASSISTANT,
    Permission.EXPORT_AI_DATA,
    Permission.USE_AI_ASSISTANT,
    Permission.SYSTEM_SETTINGS,
    Permission.EXPORT_DATA,
  ];
  return sensitivePermissions.includes(permission);
};

// Utility function: Get permission group by permission
export const getPermissionGroup = (permission: Permission): string | null => {
  for (const [groupKey, group] of Object.entries(PERMISSION_GROUPS)) {
    if (group.permissions.includes(permission)) {
      return groupKey;
    }
  }
  return null;
};
